import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, ArrowRight, X, Pin } from 'lucide-react';
import { NEWS_CATEGORIES } from '../pages/News';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'news_banner_dismissed_id';

export default function NewsBanner() {
    const [post, setPost] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/news`, { params: { limit: 5 } });
                const dismissedId = localStorage.getItem(DISMISS_KEY);
                // First unread post (skip dismissed)
                const candidate = (data || []).find(p => !p.read && p.id !== dismissedId);
                setPost(candidate || null);
            } catch (e) { /* silent */ }
        })();
    }, []);

    if (!post) return null;

    const meta = NEWS_CATEGORIES.find(c => c.value === post.category) || NEWS_CATEGORIES[0];
    const Icon = meta.icon;

    const dismiss = (e) => {
        e.stopPropagation();
        localStorage.setItem(DISMISS_KEY, post.id);
        setPost(null);
    };

    const open = () => navigate('/news');

    return (
        <div
            onClick={open}
            className="cursor-pointer relative rounded-xl border border-indigo-300 dark:border-indigo-700 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 p-4 mb-6 hover:shadow-md transition-shadow"
            data-testid="news-banner"
        >
            <div className="flex items-start gap-3 pr-8">
                <div className={`${meta.bg} text-white p-2 rounded-lg flex-shrink-0`}>
                    <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
                            {meta.label}
                        </span>
                        {post.is_pinned && (
                            <span className="text-[10px] inline-flex items-center gap-0.5 text-amber-600">
                                <Pin className="w-2.5 h-2.5" /> Pinned
                            </span>
                        )}
                        <span className="text-[10px] text-rose-500 font-bold">• NEW</span>
                    </div>
                    <p className="font-semibold text-slate-900 dark:text-slate-100 truncate mt-0.5" data-testid="news-banner-title">
                        {post.title}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">
                        {post.body}
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2 inline-flex items-center gap-1">
                        Read more <ArrowRight className="w-3 h-3" />
                    </p>
                </div>
            </div>
            <button
                type="button"
                onClick={dismiss}
                className="absolute top-2 right-2 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700"
                aria-label="Dismiss"
                data-testid="news-banner-dismiss"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
